import { useState, useEffect } from "react";
import { AlertTriangle, CheckCircle, Search } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import type { WorkflowItem } from "@/hooks/useWorkflowDefaults";

interface ErrorCodeDiagnosticProps {
  errorCodes: WorkflowItem[];
  onConfirm: (selectedIds: string[]) => void;
  disabled?: boolean;
}

export function ErrorCodeDiagnostic({ errorCodes, onConfirm, disabled }: ErrorCodeDiagnosticProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");

  const filtered = errorCodes.filter(
    (ec) =>
      ec.title.toLowerCase().includes(search.toLowerCase()) ||
      (ec.description || "").toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
        <div>
          <p className="font-medium text-foreground text-sm">Error Code Diagnostic</p>
          <p className="text-xs text-muted-foreground">
            Select all error codes identified during inspection. Linked troubleshooting and parts replacement steps will be automatically added to your workflow.
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search error codes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-9"
        />
      </div>

      {/* Selection count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {errorCodes.length} error code{errorCodes.length !== 1 ? "s" : ""} available
        </p>
        {selected.length > 0 && (
          <Badge variant="secondary" className="text-xs">
            {selected.length} selected
          </Badge>
        )}
      </div>

      {/* Error code list */}
      <ScrollArea className="max-h-[300px]">
        <div className="space-y-1.5">
          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground italic text-center py-4">
              No error codes found
            </p>
          ) : (
            filtered.map((ec) => {
              const isSelected = selected.includes(ec.id);
              return (
                <label
                  key={ec.id}
                  className={`flex items-start gap-3 py-2.5 px-3 rounded-lg cursor-pointer transition-colors border ${
                    isSelected
                      ? "bg-primary/5 border-primary/20"
                      : "hover:bg-muted/50 border-transparent"
                  }`}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggle(ec.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{ec.title}</p>
                    {ec.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">{ec.description}</p>
                    )}
                  </div>
                </label>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Confirm button */}
      <Button
        onClick={() => onConfirm(selected)}
        disabled={disabled}
        className="w-full gap-2"
      >
        <CheckCircle className="w-4 h-4" />
        {selected.length === 0
          ? "Continue Without Error Codes"
          : `Confirm ${selected.length} Error Code${selected.length !== 1 ? "s" : ""}`}
      </Button>
    </div>
  );
}
